import * as THREE from "three";

/**
 * The ground's noise and geometry helpers (groundGeometry.ts and the layers over it): the shared micro-relief, the
 * lateral wander, the cliff strata, value noise, and normals that never come out zero.
 */

/** The noise amplitudes (GroundMesh.Relief and GroundMesh.Warp are these). */
export const NoiseAmp = {
  /** Vertical relief of the smooth ground noise, in world units. */
  Relief: 0.06,
  /** Lateral wander applied to every ground vertex, so no plateau edge or island rim runs straight along the grid. */
  Warp: 0.2,
} as const;

/**
 * `computeVertexNormals`, with any zero-length normal replaced by straight up.
 *
 * A vertex shared only by degenerate (zero-area) triangles gets a zero normal, which normalises to NaN in the
 * shader and lights as pure white. That is what the white shards in the gorges were. The geometry that produces
 * them is a bug in its own right, but a renderer should not paint a hole in the world when it meets one.
 */
export function safeNormals(g: THREE.BufferGeometry): void {
  g.computeVertexNormals();
  const n = g.getAttribute("normal") as THREE.BufferAttribute;
  for (let i = 0; i < n.count; i++) {
    const x = n.getX(i), y = n.getY(i), z = n.getZ(i);
    if (x * x + y * y + z * z > 1e-8) continue;
    n.setXYZ(i, 0, 1, 0);
  }
  n.needsUpdate = true;
}

/** The shared smooth relief: identical for any two cells that meet, so plateaus weld without a seam. */
export function relief(x: number, z: number): number {
  return (vnoise(x * 0.37, z * 0.37) - 0.5) * NoiseAmp.Relief * 2 + (vnoise(x * 1.13 + 7, z * 1.13 + 3) - 0.5) * NoiseAmp.Relief;
}
/**
 * A slow lateral wander applied to every ground vertex. It is a pure function of the *unwarped* world position, so any
 * two vertices that were welded stay welded — but every plateau edge and the island rim stop running dead straight
 * along the grid, which is what made the map read as a stack of boxes.
 */
export function warp(x: number, z: number): [number, number] {
  const w = NoiseAmp.Warp;
  const lo = (sx: number, sz: number) => (vnoise(x * 0.23 + sx, z * 0.23 + sz) - 0.5) * 2;
  const hi = (sx: number, sz: number) => (vnoise(x * 0.71 + sx, z * 0.71 + sz) - 0.5) * 2;
  return [(lo(11, 5) * 0.7 + hi(3, 9) * 0.3) * w, (lo(31, 17) * 0.7 + hi(23, 41) * 0.3) * w];
}
/** Horizontal displacement of a cliff face at height y along an edge: wavy sedimentary bands. */
export function strata(y: number, along: number): number {
  return vnoise(along * 0.5, y * 4.5) * 0.7 + vnoise(along * 1.7 + 3, y * 9.0) * 0.3;
}
export function hash2(x: number, y: number): number { const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453; return s - Math.floor(s); }
export function vnoise(x: number, y: number): number {
  const ix = Math.floor(x), iy = Math.floor(y); let fx = x - ix, fy = y - iy;
  fx = fx * fx * (3 - 2 * fx); fy = fy * fy * (3 - 2 * fy);
  const a = hash2(ix, iy), b = hash2(ix + 1, iy), c = hash2(ix, iy + 1), d = hash2(ix + 1, iy + 1);
  return (a + (b - a) * fx) * (1 - fy) + (c + (d - c) * fx) * fy;
}
export function smoothstepN(a: number, b: number, v: number): number { const t = Math.min(1, Math.max(0, (v - a) / (b - a))); return t * t * (3 - 2 * t); }
